<template>
  <div class="quick-operations">
    <span class="quick-btn" @click="handleToSearchClick"><van-icon name="search" /></span>
    <span class="quick-btn" @click="handleWishListClick">
      <van-icon name="like-o" v-show="!isWish" />
      <van-icon name="like" v-show="isWish" color="#DD3131" />
    </span>
    <span v-if="!isCps" class="quick-btn" @click="handleShareClick"><van-icon name="share-o" /></span>
    <van-popover
      v-model:show="showPopover"
      :actions="actions"
      theme="dark"
      placement="bottom-end"
      @select="handleMoreItemClick"
    >
      <template #reference>
        <span class="quick-btn"><van-icon name="ellipsis" /></span>
      </template>
    </van-popover>
    
    <KSShareStoreSharePopup v-model:show="sharePopupShow" @share:click="handleShareItemClick">
      <div class="share-title">Share</div>
      <div class="share-product-card">
        <img v-lazy="productImage" alt="" class="product-image" />

        <p class="product-name">{{ productName }}</p>
      </div>
    </KSShareStoreSharePopup>
  </div>
</template>

<script setup lang="ts">
import { wishListing, delWishListing } from '@/api/models/product';
import { share } from '@/components/KSShareStore/share'
import { getShareLink, type IShareLinkType } from '@/api/models/share'
import { buildShareLinkTrafficSourceParams } from '@/plugins/utils'
import { customEventFn, eventName, webClickFn } from '@/plugins/bi-sa.client'
import type { ITrackConfig } from '../mixins'
import { getTrafficSourceParts } from '@/utils/traffic-source'

const props = defineProps<{
  isCps?: boolean
  listingId?: number,
  skuId?: number,
  productImage: string,
  isWish: boolean
  productName: string,
  trackBaseData?: ITrackConfig
}>()
const emits = defineEmits<{
  (e: 'to:chat'): void
  (e: 'add:wish', isWish: boolean): void
}>()

interface IAction { text: string, icon: string, path: string }

const { $fcWidgetOpen, $sensors, $SENSORS_EVENT } = useNuxtApp()
const router = useRouter()
const showPopover = ref<boolean>(false)
const actions: IAction[] = [
  { text: 'Account', icon: 'contact-o', path: '/user-center' },
  { text: 'Wish list', icon: 'like-o', path: '/wish-list' },
  { text: 'Home', icon: 'wap-home-o', path: '/' },
  { text: 'Message', icon: 'chat-o', path: '/message' },
  { text: 'FAQ', icon: 'question-o', path: 'FAQ' }
]

const handleFcWidgetOpenClick = async() => {
  showLoadingToast({ duration: 0 })
  try {
    await $fcWidgetOpen()

    closeToast()
  } catch(e) {
    showToast('fresh-chat loading error')
  }
}

const handleToSearchClick = () => {
  router.push('/search-input?first_page=productDetail')
}
const handleWishListClick = () => {
  const route = useRoute()
  const { isAd } = route.query || {}
  const source = props.trackBaseData?.source || (route.query.source as string) || ''
  const [
    internalLatestTrafficSourceModule = '',
    internalLatestTrafficSourceType = '',
    insideLatestTrafficSourceValue = '',
    internalFirstTrafficSourcePage = '',
    internalLatestTrafficSourcePage = ''
  ] = getTrafficSourceParts(source, props.trackBaseData?.trafficSourceMeta)

  const isAdVal = !!props.trackBaseData?.isAd || isAd === 'ad'
  const isNewTagVal = !!props.trackBaseData?.isNewTag
  const isChoiceTagVal = !!props.trackBaseData?.isChoiceTag
  const requestIdVal = props.trackBaseData?.requestId || ''

  const action = props.isWish
    ? delWishListing(
        props.listingId!,
        internalLatestTrafficSourceModule,
        internalLatestTrafficSourceType,
        insideLatestTrafficSourceValue,
        isAdVal,
        internalFirstTrafficSourcePage,
        internalLatestTrafficSourcePage
      )
    : wishListing(
        props.listingId!,
        internalLatestTrafficSourceModule,
        internalLatestTrafficSourceType,
        insideLatestTrafficSourceValue,
        isAdVal,
        isNewTagVal,
        requestIdVal,
        internalFirstTrafficSourcePage,
        internalLatestTrafficSourcePage,
        isChoiceTagVal
      )

  action.then(() => {
    emits('add:wish', !props.isWish)
  })
}

// const source = (useRoute().query.source || '') as string
const sharePopupShow = ref<boolean>(false)
const shareLinkTypeData = ref({})
const handleShareClick = async () => {
  const params: IShareLinkType = {
    sourceType: 'Listing',
    sourceId: props.listingId,
    generateShortLink: false,
    ...buildShareLinkTrafficSourceParams('Listing')
  }
  shareLinkTypeData.value = await getShareLink(params)
  sharePopupShow.value = true

  $sensors.track(
    $SENSORS_EVENT.clickToShareButton,
    { 
      shareType: 'shareProduct'
    }
  )
  customEventFn(eventName.clickToShareButton, {
    share_type: 'shareProduct'
  })
  webClickFn(3, 'shareIcon', true)
}
const handleShareItemClick = (e: any) => {
  let url = `${useCurrentHost()}/listing/${props.listingId}?source=productDetails-share`
  if (shareLinkTypeData.value.code === 0) {
    // 取接口短链
    const shareLinkTypeList = shareLinkTypeData.value?.data?.templates || []
    shareLinkTypeList.forEach((item) => {
      if (e.key === 'Facebook' && item.channel === 'facebook' && item.code === 'link') {
        url = item.textEn
      }
      else if (e.key === 'WhatsApp' && item.channel === 'whatsapp' && item.code === 'link') {
        url = item.textEn
      }
      else if ((e.key === 'SMS' || e.key === 'CopyLink') && item.channel === '' && item.code === 'link') {
        url = item.textEn
      }
    })
  }
  share(
    e,
    { 
      href: url,
      title: '🌟 Check out this amazing product! 😍 Click the link and see why everyone’s raving about it! 🚀🛒',
      shareContents: props.productName
    },
    'shareProduct'
  )
}

const handleMoreItemClick = async(e: IAction) => {
  if(/^http(s)?:\/\//.test(e.path)) {
     window.open(e.path)
  } else if(e.path === 'FAQ') {
    // freshchat sdk
    await handleFcWidgetOpenClick()
  } else {
    router.push(e.path)
  }
}
</script>

<style scoped lang="scss">
.quick-operations{
  display: flex;
  align-items: center;
  position: absolute;
  right: 20px;
  top: 20px;
}
.quick-operations::v-deep(.share-list) {
  padding: 60px 30px;
}
.quick-btn{
  margin-left: 20px;
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, .3);
  overflow: hidden;
  border-radius: 50%;
  i { font-size: 38px; color: #fff;}
}

.share-title{
  color: #333;
  font-weight: 600;
  font-size: 36px;
  text-align: center;
  padding: 30px 0;
}
.share-product-card{
  display: flex;
  align-items: center;
  background-color: #f2f2f2;
  padding: 20px;
  margin: 0 30px;
  border-radius: 10px;
  overflow: hidden;
  .product-image{
    width: 120px;
    border-radius: 10px;
    min-width: 120px;
    height: 120px;
    margin-right: 20px;
    object-fit: contain;
    background-color: #fff;
  }
  .product-name{
    flex: 1;
    @include ellipsisMultiline(2);
    font-size: 26px;
    line-height: 40px;
    color: #333;
  }
}
</style>
